import { useAuth } from 'src/utils';
import { useEffect, useState } from 'react';
import { Box, Typography, Divider, UserFlag, Button, ShareIcon, Loader, CircularProgress } from "src/components";
import { getTransportMode, buildStatistics } from 'src/data';
import { api, tripTrackingShareUrl } from 'src/utils';
import { useIsMobile } from 'src/hooks';
import { format } from 'date-fns';
import StatisticsCard from './statistics-card';
import StatisticsMap from './statistics-map';

import bgImage from 'src/assets/placeholders/grey-300x200.png';
import vehicleBackgroundImage from 'src/assets/statistics-vehicle-bg.png';
import globeBackgroundImage from 'src/assets/globe.png';
import CalendarIcon from 'src/assets/icons/calendar.svg?react';
import SpeedIcon from 'src/assets/icons/speedometer.svg?react';

export default function Statistics({ 
    isPublicPortal, 
    steps, //Used for Public Portal
    type = "trip", 
    title, trips, 
    setSelectedTripSteps, 
    totalTripKms, 
    totalTripTime }) {

    const { user } = useAuth();
    const isMobile = useIsMobile();
    const [allSteps, setAllSteps] = useState(steps || []);
    const [loading, setLoading] = useState(false);

    const isGlobalStats = (type === "global");
    const transportMode = getTransportMode(user?.transportMode);
    const totalDistance = totalTripKms !== null ? Math.round(totalTripKms).toLocaleString('en-US') : null;

    useEffect(() => {
        const fetchAllSteps = async () => {
            if (!isGlobalStats || !trips?.length) return;

            try {
                setLoading(true);
                const stepArrays = await Promise.all(
                    trips.map(trip =>
                        api.get(`/trips/${trip.id}/steps`).then(res => res.data)
                    )
                );

                const sortedStepArrays = stepArrays.map(steps =>
                    steps.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
                );

                setAllSteps(sortedStepArrays);
                setSelectedTripSteps(sortedStepArrays);
            } catch (error) {
                console.error("Failed to fetch global steps:", error);
            } finally {
                setLoading(false);
            }
        };

        if (!isPublicPortal) {
            fetchAllSteps();
        }
    }, [isGlobalStats, trips, isPublicPortal]);


    const stats = buildStatistics(allSteps, trips, type, transportMode.label);

    const formattedEstimatedFuelCosts = Math.round(stats.fuelCostEstimation || 0).toLocaleString('en-US');
    const formattedStart = stats.startDate ? format(new Date(stats.startDate), "do MMM yyyy") : "N/A";
    const formattedEnd = stats.endDate ? format(new Date(stats.endDate), "do MMM yyyy") : "N/A";

    var referralLink = isGlobalStats ? tripTrackingShareUrl(user?.id) : tripTrackingShareUrl(user?.id, trips[0]?.id);

    const shareData = {
        title: 'Checkout my Pan American Highway Journey!',
        text: 'Checkout my Pan American Highway Journey!',
        url: referralLink || window.location.href,
    };

    const handleInviteClick = async () => {
        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                console.error('Share failed:', err);
            }
        } else {
            // Fallback: copy link to clipboard
        try {
            await navigator.clipboard.writeText(shareData.url);
            alert('Invite link copied to clipboard!');
        } catch {
            alert('Sharing is not supported on this browser.');
        }
        }
    };

    if (!user && !isPublicPortal) return null;

    if (loading) return <Loader />;

    const getLoader = (size) => {
        if (allSteps?.length > 0) {
            return <CircularProgress size={size} thickness={4} />
        }
        return "0";
    };

    return (
        <Box className="scrollBox" sx={{ height: '100%', pb: isGlobalStats ? (isMobile ? 10 : 2) : 0, pr: isGlobalStats ? 2 : 0 }}>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap', 
                    mt: 1,
                    mb: 2,
                    gap: 2, 
                }}
                >
                <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ flex: 1, minWidth: 0 }} // Ensures it doesn't push the button off
                >
                    {title}
                </Typography>

                {!isPublicPortal && (
                    <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    startIcon={<ShareIcon fontSize="small" />}
                    sx={{
                        borderRadius: 100,
                        whiteSpace: 'nowrap',      
                        flexShrink: 0           
                    }}
                    onClick={handleInviteClick}
                    >
                    Share Stats
                    </Button>
                )}
            </Box>


            {/* Transport + PanAm */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                <StatisticsCard
                    icon={transportMode?.icon}
                    backgroundImage={vehicleBackgroundImage}
                    tallCard={!isGlobalStats}
                />
                <StatisticsCard
                    value={stats?.panAmPercentage || "0"}
                    valueMeasurement="%"
                    label="of the PanAm"
                    backgroundImage={globeBackgroundImage}
                    backgroundOverlay={"rgba(255, 255, 255, 0.2)"}
                    textColor="#fff"
                    tallCard={!isGlobalStats}
                >
                <Typography variant="subtitle1" fontWeight={600} sx={{ position: 'absolute', bottom: 10 }}>
                    of the PanAm
                </Typography>
                </StatisticsCard>
            </Box>

            {/* Countries Visited */}
            <Box
                sx={{
                    border: '1px solid #D3D3D3',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    mt: 2,
                    backgroundImage: `url(${bgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
                <Typography variant="h5" fontWeight={600} mb={2}>
                    Countries Visited
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {stats.countriesVisited.map(code => (
                        <UserFlag key={code} countryCode={code} size={35} />
                    ))}
                </Box>
            </Box>

            <Typography variant="h5" fontWeight={600} mb={1} mt={2}>
                Pan American Highway Map
            </Typography>

            <StatisticsMap countriesVisited={stats.countriesVisited} />

            {/* Distance + Duration Cards */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, mt: 2 }}>
                <StatisticsCard 
                    icon={SpeedIcon}
                    iconSize={50}
                    label="driven" 
                    value={totalDistance !== null ? totalDistance : getLoader(22)} 
                    valueMeasurement="km" />

                <StatisticsCard 
                    icon={CalendarIcon}
                    iconSize={50}
                    label="on the road" 
                    value={stats.totalDurationInDays || "0"} 
                    valueMeasurement={
                        stats.totalDurationInDays === 1 ? "day" : "days"
                    } />
            </Box>

            {/* Start and End Country Flags */}
            <Box
                sx={{
                    border: '1px solid #D3D3D3',
                    bgcolor: '#fff',
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    mb: 2,
                    textAlign: 'center',
                }}
                >

                {/* Left: Start Flag and Date */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <UserFlag countryCode={stats.startingCountry} size={35} />
                    <Typography variant="subtitle1" mt={1} fontWeight={600}>
                        {formattedStart}
                    </Typography>
                </Box>

                {/* Center: Large Arrow */}
                <Typography variant="h4" fontWeight={600}>
                    →
                </Typography>

                {/* Right: End Flag and Date */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <UserFlag countryCode={stats.finishingCountry} size={35} />
                    <Typography variant="subtitle1" mt={1} fontWeight={600}>
                        {formattedEnd}
                    </Typography>
                </Box>
                </Box>

            {/* Overview Section */}
            <Box
                sx={{
                borderRadius: 2,
                border: '1px solid #D3D3D3',
                bgcolor: '#fff',
                p: 2,
                mb: 2
                }}
            >
                <Typography variant="h5" fontWeight={600} mb={1}>
                    Trip Overview Stats
                </Typography>
                <Divider sx={{ my: 1 }} />
                {stats.totalTrips > 1 && (<>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Total Trips</Typography>
                        <Typography fontWeight={600}>{stats.totalTrips}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                </>)}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Total Steps</Typography>
                    <Typography fontWeight={600}>{stats.totalSteps}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Total kms</Typography>
                    <Typography fontWeight={600}>
                          {totalDistance !== null
                            ? `${totalDistance} km` 
                            : getLoader(12)
                          }
                    </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Estimated Fuel Cost</Typography>
                    <Typography fontWeight={600}>${formattedEstimatedFuelCosts || 0}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Total Drive Time</Typography>
                    <Typography fontWeight={600}>
                        {totalTripTime ? 
                            formatDuration(totalTripTime) :
                            getLoader(12)}
                    </Typography>
                </Box>
            </Box>

            {/* On The PanAm Overview Section */}
            <Box
                sx={{
                borderRadius: 2,
                border: '1px solid #D3D3D3',
                bgcolor: '#fff',
                p: 2,
                mb: 2
                }}
            >
                <Typography variant="h5">On The PanAm Highway Stats</Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Your Estimated kms</Typography>
                    <Typography fontWeight={600}>
                        {stats.panAmKmsDone.toLocaleString('en-US')} km
                    </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Total PanAm kms</Typography>
                    <Typography fontWeight={600}>
                        {stats.panAmKmsTotal.toLocaleString('en-US')} km
                    </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Completed</Typography>
                    <Typography fontWeight={600}>
                        {stats.panAmPercentage} %
                    </Typography>
                </Box>
            </Box>

            {!isPublicPortal && (<>
                <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    startIcon={<ShareIcon/>}
                    sx={{
                        borderRadius: 2,
                        padding: 2,
                    }}
                    onClick={handleInviteClick}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                        Share Trip with Friends & Family
                    </Typography>
                </Button>

                <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ whiteSpace: 'wrap', mt: 2, p: 1 }}>
                    *Give your family & friends a front-row seat: share your journey’s highlights, location, and stats via a private portal.
                </Typography>

            </>)}
        </Box>
    );
}

function formatDuration(decimalHours) {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);

  if (hours > 0 && minutes > 0) {
    return `${hours} hr ${minutes} min`;
  } else if (hours > 0) {
    return `${hours} hr`;
  } else {
    return `${minutes} min`;
  }
}